/**
 * Where you are in the app.
 *
 * A hand-rolled union rather than a router dependency: there are five places to
 * be, they are all flat, and the whole thing is forty lines. What matters is that
 * the SET IS CLOSED and every destination is spelled once — the previous version
 * scattered `pushState('/?room=' + room)` across three components, which is how
 * the front door ended up meaning "join a meeting" in the first place.
 *
 * The portal serves index.html for any extensionless path (see spa() in the
 * portal), so every one of these survives a reload and can be pasted into a chat.
 */
export type Route =
  | { name: 'dashboard' }
  | { name: 'call'; room: string }
  | { name: 'meetings' }
  | { name: 'rooms' }
  | { name: 'schedule' }
  | {
      name: 'meeting';
      room: string;
      session: string;
      /**
       * A guest's signed link to this one meeting, from their summary email.
       * They have no account, so the permission travels in the URL — which means
       * it has to survive a reload and be passed on to the API call.
       */
      token?: string;
      /**
       * Seconds into the meeting to open at.
       *
       * This is what makes a search result a citation rather than a reference.
       * It is in the URL rather than in component state because the point of a
       * citation is that it can be pasted into a message and land on the same
       * sentence.
       */
      at?: number;
    }
  /**
   * A recorded meeting, played back like a video (MN-35). `token` and `at` as
   * for 'meeting'.
   */
  | { name: 'play'; room: string; session: string; token?: string; at?: number }
  | { name: 'search'; q?: string; speaker?: string; room?: string }
  | { name: 'admin' }
  | { name: 'diagnostics' };

/** The path a route lives at. The single source of truth for every link. */
export function hrefFor(r: Route): string {
  switch (r.name) {
    case 'dashboard':
      return '/';
    case 'call':
      return r.room ? `/call?room=${encodeURIComponent(r.room)}` : '/call';
    case 'meetings':
      return '/meetings';
    case 'rooms':
      return '/rooms';
    case 'schedule':
      return '/schedule';
    case 'meeting': {
      const p = `/meetings/${encodeURIComponent(r.room)}/${encodeURIComponent(r.session)}`;
      const q = new URLSearchParams();
      if (r.token) q.set('t', r.token);
      if (r.at !== undefined) q.set('at', String(Math.floor(r.at)));
      const s = q.toString();
      return s ? `${p}?${s}` : p;
    }
    case 'play': {
      const p = `/meetings/${encodeURIComponent(r.room)}/${encodeURIComponent(r.session)}/play`;
      const q = new URLSearchParams();
      if (r.token) q.set('t', r.token);
      if (r.at !== undefined) q.set('at', String(Math.floor(r.at)));
      const s = q.toString();
      return s ? `${p}?${s}` : p;
    }
    case 'search': {
      const q = new URLSearchParams();
      if (r.q) q.set('q', r.q);
      if (r.speaker) q.set('speaker', r.speaker);
      if (r.room) q.set('room', r.room);
      const s = q.toString();
      return s ? `/search?${s}` : '/search';
    }
    case 'admin':
      return '/admin';
    case 'diagnostics':
      return '/diagnostics';
  }
}

/**
 * Read the current URL as a route.
 *
 * Anything unrecognised is the dashboard, deliberately: a stale or mistyped link
 * should land somewhere useful, and the one thing it must never do is fall
 * through to joining a call.
 */
export function parseRoute(loc: Location = window.location): Route {
  const path = loc.pathname.replace(/\/+$/, '') || '/';
  const room = () => new URLSearchParams(loc.search).get('room') ?? '';

  const params = new URLSearchParams(loc.search);

  if (path === '/call') return { name: 'call', room: room() };
  if (path === '/diagnostics') return { name: 'diagnostics' };
  if (path === '/admin') return { name: 'admin' };
  if (path === '/meetings') return { name: 'meetings' };
  if (path === '/rooms') return { name: 'rooms' };
  if (path === '/schedule') return { name: 'schedule' };
  if (path === '/search') {
    return {
      name: 'search',
      q: params.get('q') ?? undefined,
      speaker: params.get('speaker') ?? undefined,
      room: params.get('room') ?? undefined,
    };
  }

  const pm = /^\/meetings\/([^/]+)\/([^/]+)\/play$/.exec(path);
  if (pm) {
    const at = Number(params.get('at'));
    return {
      name: 'play',
      room: decodeURIComponent(pm[1]!),
      session: decodeURIComponent(pm[2]!),
      token: params.get('t') ?? undefined,
      at: Number.isFinite(at) && at > 0 ? at : undefined,
    };
  }

  const m = /^\/meetings\/([^/]+)\/([^/]+)$/.exec(path);
  if (m) {
    const at = Number(params.get('at'));
    return {
      name: 'meeting',
      room: decodeURIComponent(m[1]!),
      session: decodeURIComponent(m[2]!),
      token: params.get('t') ?? undefined,
      // A non-numeric ?at= is not an error worth showing anybody; it just means
      // the meeting opens at the top, which is where it opens anyway.
      at: Number.isFinite(at) && at > 0 ? at : undefined,
    };
  }

  return { name: 'dashboard' };
}
